import {
  Component,
  HostListener,
  Input,
  OnInit,
  ViewChild,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import {
  ApexAxisChartSeries,
  ApexChart,
  ChartComponent,
  ApexDataLabels,
  ApexXAxis,
  ApexPlotOptions,
  ApexTitleSubtitle,
  ApexYAxis,
  ApexTooltip,
} from "ng-apexcharts";

type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  title: ApexTitleSubtitle;
  tooltip: ApexTooltip;
  colors: string[];
};

@Component({
  selector: "lib-bar-chart",
  templateUrl: "./bar-chart.component.html",
})
export class BarChartComponent implements OnInit {
  @Input() horizontal: boolean;
  @Input() categorias: any;
  @Input() data: any;
  @Input() nombre: string;
  @Input() valorMaximo: number;
  @Input() title: string;

  public smallScreen: boolean;
  private readonly _BREAKPOINT_SIZE_SCREEN = 1375;

  @ViewChild("chart") chart: ChartComponent;
  public chartOptions: Partial<ChartOptions>;

  constructor(private readonly _translate: TranslateService) {
    this.smallScreen =
      window.innerWidth <= this._BREAKPOINT_SIZE_SCREEN ? true : false;
  }

  @HostListener("window:resize")
  onWindowResize() {
    console.log("window:resize");
    this.smallScreen =
      window.innerWidth <= this._BREAKPOINT_SIZE_SCREEN ? true : false;
    this.drawChart();
  }

  ngOnInit() {
    this.drawChart();
  }

  private drawChart() {
    this.chartOptions = {
      series: [
        {
          name: this.nombre,
          data: this.data,
        },
      ],
      chart: {
        type: "bar",
        height: this.horizontal
          ? (this.categorias.length + 2) * 50
          : this.smallScreen
          ? 500
          : 800,
        width: this.horizontal
          ? this.smallScreen
            ? 500
            : 900
          : this.smallScreen
          ? 500
          : 600,
        redrawOnWindowResize: true,
        fontFamily: "medium",
        animations: {
          enabled: false,
        },
        toolbar: {
          show: true,
          offsetX: 0,
          offsetY: 0,
          tools: {
            download: true,
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: '<button><i class="fas fa-table"></i></button>',
            customIcons: [],
          },
          export: {
            csv: {
              filename: undefined,
              columnDelimiter: ",",
              headerCategory: "category",
              headerValue: "value",
              dateFormatter(timestamp) {
                return new Date(timestamp).toDateString();
              },
            },
          },
          autoSelected: "zoom",
        },
      },
      tooltip: {
        enabled: true,
        style: {
          fontSize: "11.8px",
          fontFamily: "medium",
        },
        y: {
          title: {
            formatter: (seriesName) => this.nombre,
          },
          formatter: (val, opts) => {
            return isNaN(+val)
              ? val.toString()
              : Number(val).toLocaleString(this._translate.getDefaultLang(), {
                  minimumFractionDigits: 0,
                });
          },
        },
      },
      title: {
        text: this.title ? this.title : undefined,
        align: "center",
        offsetX: 0,
        offsetY: 0,
        floating: false,
        style: {
          fontSize: "14px",
          fontFamily: "medium",
          color: "#184d39",
        },
      },
      plotOptions: {
        bar: {
          dataLabels: {
            position: "top", // top, center, bottom
          },
          horizontal: this.horizontal,
        },
      },
      colors: ["#184d39"],
      dataLabels: {
        style: {
          fontSize: "11px",
          fontFamily: "medium",
          colors: ["#fff"],
        },
        offsetX: 0,
        formatter: (val: any, opts) => {
          let calculo;

          if (opts.w.globals.maxY <= 1000) {
            calculo = 0.03;
          } else if (
            opts.w.globals.maxY > 1000 &&
            opts.w.globals.maxY <= 100000
          ) {
            calculo = 0.05;
          } else if (
            opts.w.globals.maxY > 100000 &&
            opts.w.globals.maxY <= 1000000
          ) {
            calculo = 0.09;
          } else if (opts.w.globals.maxY > 1000000) {
            calculo = 0.11;
          }
          if (+val / opts.w.globals.maxY >= calculo) {
            return Math.round(+val).toLocaleString(
              this._translate.getDefaultLang(),
              { minimumFractionDigits: 0, maximumFractionDigits: 0 }
            );
          } else {
            return "";
          }
        },
      },
      xaxis: {
        categories: this.categorias,
        min: this.horizontal ? 0 : undefined,
        max: Number.isInteger(this.valorMaximo)
          ? this.valorMaximo
          : Math.round(this.valorMaximo / 10) * 10 + 10,
        tickAmount: 5,
        labels: {
          show: true,
          style: {
            fontSize: "11.8px",
            fontFamily: "medium",
            colors: "#184d39",
          },
          formatter: (val, opts) => {
            return isNaN(+val)
              ? val.toString()
              : +val >= 2000 && +val <= 2200
              ? parseInt(val.toString()).toString()
              : Number(val).toLocaleString(this._translate.getDefaultLang(), {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                });
          },
        },
      },
      yaxis: {
        min: !this.horizontal ? 0 : undefined,
        max: Number.isInteger(this.valorMaximo)
          ? this.valorMaximo
          : Math.round(this.valorMaximo / 10) * 10 + 10,
        forceNiceScale: false,
        labels: {
          show: true,
          minWidth: 75,
          maxWidth: 150,
          style: {
            colors: "#184d39",
            fontSize: "11.8px",
            fontFamily: "medium",
          },
          formatter: (val, opts) => {
            return isNaN(+val)
              ? val.toString()
              : +val >= 2000 && +val <= 2200
              ? parseInt(val.toString()).toString()
              : Number(val).toLocaleString(this._translate.getDefaultLang(), {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                });
          },
        },
      },
    };
  }
}
